import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaArrowsRotate, FaDownload } from 'react-icons/fa6';

import { VisualizationViolinDiagramContainerProps } from './types';
import { useViolinPlots } from './hooks';
import { categorizeViolinPlots, composeViolinPlotsImage } from './utils/violinPlotImage';
import { downloadBlob } from '../../utils/downloadUtils';
import { LoadingState } from './LoadingState';
import { ErrorState } from './ErrorState';
import { EmptyState } from './EmptyState';
import { ViolinPlotGrid } from './ViolinPlotGrid';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';

/**
 * VisualizationViolinDiagramContainer component
 * Fetches and displays violin plot visualizations for training data
 */
export const VisualizationViolinDiagramContainer: React.FC<VisualizationViolinDiagramContainerProps> = memo(({
  sessionId
}) => {
  const { t } = useTranslation('training');

  const {
    violinPlots,
    isLoading,
    error,
    imageErrors,
    handleRefresh,
    handleImageError
  } = useViolinPlots({ sessionId });

  const [isDownloading, setIsDownloading] = useState(false);

  // Compose all plots into a single PNG so the user doesn't have to save each one
  const handleDownloadAll = async () => {
    if (!violinPlots || isDownloading) return;
    setIsDownloading(true);
    try {
      const { inputPlots, timePlots, outputPlots } = categorizeViolinPlots(violinPlots);
      const sections = [
        { title: t('training.visualization.inputDistribution'), plots: inputPlots },
        { title: t('training.visualization.timeComponents'), plots: timePlots },
        { title: t('training.visualization.outputDistribution'), plots: outputPlots }
      ].filter(section => section.plots.length > 0);

      const blob = await composeViolinPlotsImage(sections);
      downloadBlob(blob, `violin_plots_${sessionId}.png`);
    } catch (err) {
      console.error('Failed to compose violin plots image:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={handleRefresh} />;
  }

  // Check if we have any violin plots at all
  const hasPlots = violinPlots?.plots && Object.keys(violinPlots.plots).length > 0;

  if (!hasPlots) {
    return <EmptyState />;
  }

  return (
    <div className="mt-6 sm:mt-8 rounded-xl border border-border bg-card overflow-visible shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-border">
        <div className="flex items-center gap-1.5">
          <h2 className="text-base sm:text-lg font-semibold text-foreground tracking-tight">
            {t('training.visualization.title')}
          </h2>
          <InfoTooltip text={t('tooltips.violin')} width="w-80" />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleDownloadAll}
            disabled={isDownloading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-foreground bg-card border border-border rounded-lg hover:bg-muted hover:text-foreground active:scale-[0.98] transition-all duration-150 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDownloading
              ? <FaArrowsRotate className="text-[10px] animate-spin" />
              : <FaDownload className="text-[10px]" />}
            {t('training.visualization.downloadAll')}
          </button>
          <button
            type="button"
            onClick={handleRefresh}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-foreground bg-card border border-border rounded-lg hover:bg-muted hover:text-foreground active:scale-[0.98] transition-all duration-150 shadow-sm"
          >
            <FaArrowsRotate className="text-[10px]" />
            {t('training.visualization.refresh')}
          </button>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        <ViolinPlotGrid
          violinPlots={violinPlots}
          imageErrors={imageErrors}
          onImageError={handleImageError}
        />
      </div>
    </div>
  );
});

VisualizationViolinDiagramContainer.displayName = 'VisualizationViolinDiagramContainer';

export default VisualizationViolinDiagramContainer;
